/* eslint-disable arrow-body-style */
import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { useTranslation } from '../../src/i18n';
import { colors, fontSize, boxShadows } from '../../constants/styleConstants';
import RepertorieDialog from '../RepertorieDialog';
import { getRepertoireProductionListAction, updateRepertoireModalAction } from '../../containers/Artist/actions';
import { TP } from '../../constants/consts';

const useStyles = makeStyles({
  tableContainer: {
    boxShadow: boxShadows.table,
  },
  table: {
    tableLayout: 'fixed',
  },
  tableHead: {
    border: colors.linesGray,
    backgroundColor: colors.websiteLinkColorBg,
    height: '30px',
    '& >tr>th': {
      padding: '8px 12px',
      fontSize: fontSize.smallTitle,
      color: colors.grey1,
      textTransform: 'uppercase',
    },
  },
  tbody: {
    borderRadius: '8px',
    '& >tr>td': {
      padding: '12px',
      paddingBottom: 0,
      border: 0,
    },
  },
  tBodyHeader: {
    '& >td': {
      fontSize: fontSize.medium,
      color: '#4b4b4b',
      padding: 0,
      paddingBottom: '12px !important',
      border: 0,
      fontWeight: 'bold',
    },
  },
  innerTable: {
    tableLayout: 'fixed',
    borderBottom: `1px solid ${colors.linesGray}`,
    '& >tbody>tr>td': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  firstCell: {
    fontSize: fontSize.medium,
    border: 0,
    padding: '0 0 5px 12px',
    paddingLeft: '0 !important',
  },
  cell: {
    borderBottom: 0,
    padding: '0 0 5px 12px',
  },
  retired: {
    textDecoration: 'line-through',
  },
  tBodyRow: {
    '&:last-child': {
      '& >td': {
        paddingBottom: '12px',
        '& >table': {
          borderBottom: '0 !important',
        },
      },
    },
  },
  composer: {
    color: '#555555',
    verticalAlign: 'top',
  },
  role: {
    color: colors.blueChip,
    cursor: 'pointer',
  },
  production: {
    color: colors.blueChip,
    cursor: 'pointer',
    '& span': {
      fontStyle: 'italic',
      color: '#4b4b4b',
    },
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    fontSize: fontSize.medium,
  },
});

const Role = ({ repertoireRecord, setRep }) => {
  const classes = useStyles();
  const roleName =
    get(repertoireRecord, 'workRole.name') ||
    get(repertoireRecord, 'workRole.reference') ||
    get(repertoireRecord, 'profession.name') ||
    get(repertoireRecord, 'profession.reference', '');
  const productionTotal = get(repertoireRecord, 'productionsTotal');
  const isRetired = repertoireRecord?.status === 'retire';
  return productionTotal > 0 ? (
    <>
      <TableCell
        align="left"
        className={cn(classes.role, classes.cell, { [classes.retired]: isRetired })}
        onClick={() => setRep(repertoireRecord)}
      >
        {roleName}
      </TableCell>
      <TableCell
        align="left"
        className={cn(classes.production, classes.cell, { [classes.retired]: isRetired })}
        onClick={() => setRep(repertoireRecord)}
      >
        {productionTotal}
      </TableCell>
    </>
  ) : (
    <>
      <TableCell align="left" className={cn(classes.cell, { [classes.retired]: isRetired })}>
        {roleName}
      </TableCell>
      <TableCell align="left" className={classes.cell} />
    </>
  );
};

const RepertorieTable = ({ repertoireGrouped, profile }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const handleModal = val => {
    dispatch(updateRepertoireModalAction(val));
  };

  const setRep = rep => {
    handleModal({ flag: true, rep });
    getRepetorieProductions(null, rep);
  };

  const getRepetorieProductions = (pagination, rep) => {
    dispatch(
      getRepertoireProductionListAction({
        params: {
          queryParams: {
            composer_id: rep?.work?.composer?.id,
            work_id: rep?.work?.id,
            work_role_id: rep?.workRole?.id,
            profile_id: profile?.id,
            profession_id: rep?.profession?.id,
            sort: '-year',
          },
          pagination: pagination || { limit: 10 },
        },
        others: !pagination,
      }),
    );
  };

  const tableContent = () => {
    return repertoireGrouped?.map(composerData => {
      return (
        <TableRow className={classes.tBodyRow} key={`ReptableRow${composerData?.composer?.id}`}>
          <TableCell colSpan={3}>
            <Table className={classes.innerTable}>
              <TableBody>
                <TableRow className={classes.tBodyHeader}>
                  <TableCell align="left" colSpan={3}>
                    {composerData?.composer?.name}
                  </TableCell>
                </TableRow>
                {composerData?.data?.map((worksList, key) => {
                  return (
                    <>
                      <TableRow key={`ReptableCol${key}`}>
                        <TableCell
                          rowSpan={worksList?.data?.length}
                          align="left"
                          className={cn(classes.composer, classes.firstCell)}
                        >
                          {get(worksList, 'work.name', '')}
                        </TableCell>
                        <Role repertoireRecord={worksList?.data[0]} setRep={setRep} />
                      </TableRow>
                      {worksList?.data?.slice(1)?.map(record => (
                        <TableRow key={`ReptableCol${key}`}>
                          <Role key={record?.id} repertoireRecord={record} setRep={setRep} />
                        </TableRow>
                      ))}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <div className={classes.tableContainer}>
      <TableContainer>
        <Table className={classes.table} aria-label="customized table">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell align="left">{t(`${TP}.COMPOSER_AND_WORK`)}</TableCell>
              <TableCell align="left">{t(`${TP}.m_ROLE`)}</TableCell>
              <TableCell align="left">{t(`${TP}.m_PROD`)}</TableCell>
            </TableRow>
          </TableHead>
          {repertoireGrouped?.length > 0 ? (
            <TableBody className={classes.tbody}>{tableContent()}</TableBody>
          ) : (
            <TableCell colSpan={3}>
              <div className={classes.emptyState}>{t(`${TP}.NO_RESULTS`)}</div>
            </TableCell>
          )}
        </Table>
      </TableContainer>

      <RepertorieDialog
        handleModal={handleModal}
        profile={profile}
        getRepetorieProductions={getRepetorieProductions}
      ></RepertorieDialog>
    </div>
  );
};

RepertorieTable.propTypes = {
  profile: PropTypes.object,
};
export default RepertorieTable;
